/**
 * WebChats — website-chatbot transcript viewer, shown as "Web Captures" (admin, read-only)
 *
 * Separate from the WhatsApp/Instagram inbox: web visitors are anonymous and there is
 * NO human handoff here. This is a browse/read view of persisted WebChatSession
 * transcripts (incl. visitors who never identified themselves), with a status showing
 * whether the session converted to a Lead — and a "View lead" jump straight to it.
 */
import { sanitizeAssistantText } from '@/utils/assistant-text.util';

import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CalendarMonthOutlined, Close, Language, OpenInNew, Person, SmartToy } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import apiService from '@/services/api.service';
import adminApi from '@/services/admin.api';
import StatusChip from '@/components/ui/StatusChip';
import { field, useUrlListState } from '@/hooks/admin/useUrlListState';

interface WebChatMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  metadata?: {
    agentName?: string;
    source?: 'rag' | 'openrouter';
  };
}
interface WebChatSessionSummary {
  _id: string;
  sessionId: string;
  agentId?: string;
  leadId?: string;
  visitor?: { name?: string; phone?: string; email?: string };
  messageCount: number;
  /** Appointments this session produced (by its Lead or as the originating chat). */
  appointmentCount?: number;
  source?: string;
  referer?: string;
  startedAt: string;
  lastActiveAt: string;
}
interface WebChatSessionFull extends WebChatSessionSummary {
  messages: WebChatMessage[];
}

const PAGE_SIZE = 20;

function visitorLabel(s: WebChatSessionSummary): string {
  const v = s.visitor || {};
  return v.name || v.phone || v.email || 'Anonymous';
}

function fmt(d?: string): string {
  if (!d) return '—';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

/** "5 Oct 2026, 11:04 PM" — compact, locale-aware, and unambiguous across d/m vs m/d. */
function fmtShort(d?: string): string {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Secondary line under the visitor name: whatever contact detail they shared, else a
 *  short session tag — so a column of "Anonymous" rows is still tell-apart-able. */
function visitorSubline(s: WebChatSessionSummary): string {
  const v = s.visitor || {};
  const contact = [v.phone, v.email].filter(Boolean).join(' · ');
  return contact || `Session ${s.sessionId.slice(-6)}`;
}

/** Page lives in the URL so opening a transcript and coming back does not drop
 *  you on page 1. `pageIndex` keeps the URL human (`?page=3`) while the
 *  component stays 0-indexed for TablePagination. */
const WEBCHATS_URL_SPEC = { page: field.pageIndex() };

const WebChats: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathPrefix = location.pathname.startsWith('/admin') ? '/admin' : '';
  /** The Leads page opens that lead's detail popup straight from `?lead=<id>`. */
  const leadPath = (leadId: string) =>
    `${pathPrefix}/activity/leads?lead=${encodeURIComponent(leadId)}`;

  /** Appointments page, narrowed to this session's lead (or, with no lead, this website-chat session). */
  const appointmentsPath = (s: WebChatSessionSummary) =>
    `${pathPrefix}/appointments?${
      s.leadId ? `lead=${encodeURIComponent(s.leadId)}` : `session=${encodeURIComponent(s.sessionId)}`
    }`;

  const [navError, setNavError] = useState<string | null>(null);
  /**
   * A phone number points at the Contact CRM record. Contacts are keyed by conversation
   * id (see Leads.tsx::openContactForLead, which this mirrors), so the number is looked
   * up rather than guessed. Plain navigate() — pushes history, so Back returns to this
   * exact page of Web Captures (page lives in the URL).
   */
  const openContactForPhone = async (phone: string) => {
    try {
      const res = await adminApi.getConversations({ search: phone, limit: 1 });
      const match = ((res.data as any[]) || [])[0];
      if (match?._id) navigate(`${pathPrefix}/contacts/${match._id}`);
      else setNavError('No contact record exists for this number yet.');
    } catch {
      setNavError('Could not open the contact record.');
    }
  };

  const [sessions, setSessions] = useState<WebChatSessionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const { state: query, patch: patchQuery } = useUrlListState(WEBCHATS_URL_SPEC);
  const page = query.page; // 0-indexed for TablePagination
  const setPage = (value: number) => patchQuery({ page: value });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openId, setOpenId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<WebChatSessionFull | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const res = await apiService.get(
          `/admin/webchat/sessions?limit=${PAGE_SIZE}&skip=${page * PAGE_SIZE}`
        );
        if (!mounted) return;
        setSessions(res?.items || []);
        setTotal(res?.total || 0);
      } catch (e: any) {
        if (mounted) setError(e?.response?.data?.error || 'Failed to load web captures');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [page]);

  const openTranscript = async (sessionId: string) => {
    setOpenId(sessionId);
    setTranscript(null);
    setTranscriptLoading(true);
    try {
      const res = await apiService.get(`/admin/webchat/sessions/${encodeURIComponent(sessionId)}`);
      setTranscript(res?.session || null);
    } catch {
      setTranscript(null);
    } finally {
      setTranscriptLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          Web Captures
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Website chatbot conversations — including anonymous visitors. No human handoff; a
          session becomes a lead once a phone or email is shared. {total} total.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {sessions.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Language sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            No web captures yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Conversations from your website chatbot will appear here.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ overflowX: 'auto', borderRadius: 3 }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Visitor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Messages</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Last active</TableCell>
                  <TableCell align="right" sx={{ width: 190 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s) => {
                  const isLead = Boolean(s.leadId);
                  const named = Boolean(s.visitor?.name || s.visitor?.phone || s.visitor?.email);
                  return (
                    <TableRow
                      key={s._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openTranscript(s.sessionId)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '0.8125rem',
                              fontWeight: 600,
                              bgcolor: named ? 'primary.main' : '#e2e8f0',
                              color: named ? '#fff' : 'text.disabled',
                            }}
                          >
                            {named ? (
                              visitorLabel(s).charAt(0).toUpperCase()
                            ) : (
                              <Person sx={{ fontSize: 18 }} />
                            )}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={named ? 'text.primary' : 'text.secondary'}
                              noWrap
                            >
                              {visitorLabel(s)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{ display: 'block' }}
                            >
                              {s.visitor?.phone ? (
                                <Tooltip title="Open contact">
                                  <Link
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void openContactForPhone(s.visitor!.phone!);
                                    }}
                                    sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                                  >
                                    {s.visitor.phone}
                                  </Link>
                                </Tooltip>
                              ) : null}
                              {s.visitor?.phone && s.visitor?.email ? ' · ' : null}
                              {s.visitor?.phone ? s.visitor?.email : visitorSubline(s)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {isLead ? (
                          <StatusChip status="active" label="Lead captured" dot />
                        ) : (
                          <StatusChip status="inactive" label="Anonymous" dot />
                        )}
                      </TableCell>
                      <TableCell align="right">{s.messageCount}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {s.source || 'website'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Started ${fmt(s.startedAt)}`}>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {fmtShort(s.lastActiveAt)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {(s.appointmentCount ?? 0) > 0 && (
                          <Tooltip
                            title={`${s.appointmentCount} appointment${s.appointmentCount === 1 ? '' : 's'}`}
                          >
                            <IconButton
                              size="small"
                              sx={{ mr: 0.5 }}
                              aria-label="View appointments"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(appointmentsPath(s));
                              }}
                            >
                              <CalendarMonthOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isLead && (
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(leadPath(s.leadId!));
                            }}
                          >
                            View lead
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          )}
        </>
      )}

      <Snackbar
        open={Boolean(navError)}
        autoHideDuration={4000}
        onClose={() => setNavError(null)}
        message={navError}
      />

      {/* Transcript dialog */}
      <Dialog
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Typography component="span" fontWeight={600} noWrap sx={{ fontSize: 'inherit' }}>
              {transcript ? visitorLabel(transcript) : 'Transcript'}
            </Typography>
            {transcript?.leadId && <StatusChip status="active" label="Lead" dot />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {transcript?.leadId && (
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                onClick={() => navigate(leadPath(transcript.leadId!))}
              >
                View lead
              </Button>
            )}
            <IconButton size="small" onClick={() => setOpenId(null)} aria-label="Close">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
          {transcriptLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !transcript?.messages?.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No messages in this session.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
              {transcript.messages.map((m, i) => {
                const isUser = m.role === 'user';
                return (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '78%',
                        px: 1.75,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: isUser ? 'primary.main' : '#fff',
                        color: isUser ? '#fff' : 'text.primary',
                        border: isUser ? 'none' : '1px solid #e2e8f0',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                        {isUser ? (
                          <Person sx={{ fontSize: 14, opacity: 0.8 }} />
                        ) : (
                          <SmartToy sx={{ fontSize: 14, opacity: 0.6 }} />
                        )}
                        <Typography variant="caption" sx={{ opacity: 0.75 }}>
                          {isUser ? 'Visitor' : m.metadata?.agentName || 'Bot'} · {fmt(m.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {isUser ? m.content : sanitizeAssistantText(m.content)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WebChats;
